import React, { useEffect, useMemo, useState, useRef } from "react";
import { MapContainer, TileLayer, useMap, CircleMarker, Circle, ZoomControl } from 'react-leaflet';
import CustomMarker from '../../components/CustomMarker';
import Invader from '../../models/Invader';
import "leaflet/dist/leaflet.css";
import PageLayout from '../layout/PageLayout'
import {useAuth0} from "@auth0/auth0-react";
import { InvaderService } from "../../services/InvaderService"
import { GeocodingResult } from "../../services/GeocodingService";
import LocationSearchBar from "./LocationSearchBar";
import "./MapPage.css";

// Distance approchée en mètres entre deux points (équirectangulaire, suffisant à l'échelle d'une rue)
const distanceMeters = (a: [number, number], b: [number, number]): number => {
    const toRad = Math.PI / 180;
    const dLat = (b[0] - a[0]) * toRad;
    const dLng = (b[1] - a[1]) * toRad * Math.cos(((a[0] + b[0]) / 2) * toRad);
    return Math.sqrt(dLat * dLat + dLng * dLng) * 6371000;
};

// Centre la carte une seule fois lors du premier positionnement GPS, sauf si
// le point de vue a déjà été revendiqué par une recherche manuelle (RG-07)
const CenterMapOnUser = ({
    position,
    viewportClaimed,
}: {
    position: [number, number];
    viewportClaimed: React.MutableRefObject<boolean>;
}) => {
    const map = useMap();

    useEffect(() => {
        if (position && !viewportClaimed.current) {
            map.setView(position, 13);
            viewportClaimed.current = true;
        }
    }, [map, position, viewportClaimed]);

    return null;
};

// Recentre/zoome la carte sur le lieu sélectionné via la recherche, en
// s'adaptant à son étendue (pays, ville, adresse) grâce au boundingbox Nominatim
const FlyToLocation = ({ location }: { location: GeocodingResult | null }) => {
    const map = useMap();

    useEffect(() => {
        if (location) {
            map.fitBounds([
                [location.boundingBox[0], location.boundingBox[2]],
                [location.boundingBox[1], location.boundingBox[3]],
            ]);
        }
    }, [map, location]);

    return null;
};

const UserLocationMarker = ({ position }: { position: [number, number] }) => {
    return (
        <>
            <Circle
                center={position}
                radius={50}
                pathOptions={{
                    color: '#4285F4',
                    fillColor: '#4285F4',
                    fillOpacity: 0.15,
                    weight: 1
                }}
            />
            <CircleMarker
                center={position}
                radius={8}
                pathOptions={{
                    color: '#fff',
                    fillColor: '#4285F4',
                    fillOpacity: 1,
                    weight: 3
                }}
            />
        </>
    );
};

const MapPage = () => {
    const [invaders, setInvaders] = useState<Invader[]>([]);
    const [onlyToFlash, setOnlyToFlash] = useState(false);
    const [onlyHighValue, setOnlyHighValue] = useState(false);
    const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
    const [searchedLocation, setSearchedLocation] = useState<GeocodingResult | null>(null);
    // Partagé entre CenterMapOnUser et la recherche manuelle : une fois le point
    // de vue revendiqué (GPS ou recherche), un relevé GPS ultérieur ne doit plus
    // recentrer la carte automatiquement (RG-07).
    const viewportClaimed = useRef(false);
    const { getAccessTokenSilently } = useAuth0();

    const handleLocationSelected = (result: GeocodingResult) => {
        viewportClaimed.current = true;
        setSearchedLocation(result);
    };

    // Liste affichée, dérivée des filtres — la liste source reste intacte
    const displayedInvaders = useMemo(
        () => invaders.filter((invader: Invader) =>
            (!onlyToFlash || invader.isOk())
            && (!onlyHighValue || (invader.value ?? 0) >= 50)),
        [invaders, onlyToFlash, onlyHighValue]
    );

    // Callback pour mettre à jour l'état captured d'un invader
    const handleInvaderCaptured = (invaderId: string) => {
        setInvaders((invadersList) =>
            invadersList.map((inv: Invader) => {
                if (inv.id === invaderId) {
                    inv.captured = true;
                }
                return inv;
            }));
    };

    // Callback pour retirer la capture d'un invader (déflash)
    const handleInvaderUncaptured = (invaderId: string) => {
        setInvaders((invadersList) =>
            invadersList.map((inv: Invader) => {
                if (inv.id === invaderId) {
                    inv.captured = false;
                }
                return inv;
            }));
    };

    // Callback pour mettre à jour le statut d'un invader
    const handleInvaderStatusChange = (invaderId: string, newStatus: string) => {
        setInvaders((invadersList) =>
            invadersList.map((inv: Invader) => {
                if (inv.id === invaderId) {
                    inv.status = newStatus;
                }
                return inv;
            }));
    };

    useEffect(() => {
        const handleNewInvader = (newInvader: Invader) => {
            setInvaders((prevInvaders) => [...prevInvaders, newInvader]);
        };

        InvaderService.fetchStream(getAccessTokenSilently, handleNewInvader).catch((error) =>
            console.error("Erreur lors de la récupération du flux des invaders :", error)
        );

        if (!navigator.geolocation) {
            console.error("La géolocalisation n'est pas prise en charge par ce navigateur.");
            setUserPosition([48.8566, 2.3522]); // Paris par défaut
            return;
        }

        // Suivi continu via watchPosition : l'OS pousse les mises à jour dès que
        // la position change et gère lui-même l'économie d'énergie du GPS, là où
        // un polling getCurrentPosition force un fix complet à chaque tick.
        let watchId: number | null = null;

        const startWatch = () => {
            if (watchId !== null) return;
            watchId = navigator.geolocation.watchPosition(
                (position) => {
                    const next: [number, number] = [position.coords.latitude, position.coords.longitude];
                    // Ignorer les micro-variations (< ~5 m) pour éviter les re-rendus inutiles
                    setUserPosition((prev) => (prev && distanceMeters(prev, next) < 5 ? prev : next));
                },
                (error) => {
                    console.error('Erreur de géolocalisation :', error);
                    setUserPosition((prev) => prev ?? [48.8566, 2.3522]); // Paris par défaut
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 5000
                }
            );
        };

        const stopWatch = () => {
            if (watchId !== null) {
                navigator.geolocation.clearWatch(watchId);
                watchId = null;
            }
        };

        // Couper le GPS quand l'onglet est en arrière-plan : la principale
        // économie de batterie, le suivi reprend au retour sur la page.
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                startWatch();
            } else {
                stopWatch();
            }
        };

        startWatch();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopWatch();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [getAccessTokenSilently]);

    return (
        <PageLayout particleDuration={10}>
            <div className="map-page">
                <div className="map-page__map">
                    <LocationSearchBar onLocationSelected={handleLocationSelected} />
                    <MapContainer
                        center={userPosition || [48.8566, 2.3522]}
                        zoom={13}
                        style={{ height: '100%' }}
                        zoomControl={false}
                    >
                        <ZoomControl position="bottomright" />
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {displayedInvaders.map((invader: Invader) => (
                            <CustomMarker
                                key={invader.id}
                                invader={invader}
                                onCapture={handleInvaderCaptured}
                                onUncapture={handleInvaderUncaptured}
                                onStatusChange={handleInvaderStatusChange}
                            />
                        ))}
                        {userPosition && <CenterMapOnUser position={userPosition} viewportClaimed={viewportClaimed} />}
                        {userPosition && <UserLocationMarker position={userPosition} />}
                        <FlyToLocation location={searchedLocation} />
                    </MapContainer>
                </div>
                {/* Barre d'actions en bas, façon tab bar native : gros items
                    icône + libellé répartis sur toute la largeur */}
                <nav className="map-page__toolbar">
                    <button
                        className={`map-action ${onlyToFlash ? "map-action--active" : ""}`}
                        onClick={() => setOnlyToFlash((value) => !value)}
                    >
                        <span className="map-action__icon">⚡</span>
                        <span className="map-action__label">À flasher</span>
                    </button>
                    <button
                        className={`map-action ${onlyHighValue ? "map-action--active" : ""}`}
                        onClick={() => setOnlyHighValue((value) => !value)}
                    >
                        <span className="map-action__icon">⭐</span>
                        <span className="map-action__label">50 pts et +</span>
                    </button>
                    <div className="map-action map-action--info" aria-live="polite">
                        <span className="map-action__icon">👾</span>
                        <span className="map-action__label">{displayedInvaders.length} affichés</span>
                    </div>
                </nav>
            </div>
        </PageLayout>
    );
};

export default MapPage;